import { useEffect, useState } from "react";
import axios from "axios";

// Fallback used ONLY when the API is unreachable AND the localStorage
// cache is empty (e.g. first-ever visitor on a fresh browser during a
// backend outage). Bumped periodically so the UI never looks stale —
// but the vast majority of visitors get the live number from
// /api/google-reviews/public via the effect below.
// Sep 2026 — the website copy says "294+ Five-Star Reviews", so
// `total` here really represents the 5-star count (backend field
// `five_star_reviews`). If the backend doesn't return it (older
// deploy), we fall back to `total_reviews` for compatibility.
const FALLBACK = { rating: 5.0, total: 0 };
const CACHE_KEY = "oc_review_stats_v2";
const TTL_MS = 60 * 60 * 1000; // 1 hour

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { rating, total, ts } = JSON.parse(raw);
    if (Date.now() - ts > TTL_MS) return null;
    if (!rating || !total) return null;
    return { rating, total };
  } catch { return null; }
};

const writeCache = (rating, total) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ rating, total, ts: Date.now() }));
  } catch { /* ignore */ }
};

/**
 * Returns the live Google review stats:
 *   { rating, total, ratingLabel, totalLabel, totalPlusLabel, loading, isLive }
 *
 * - rating is a Number (e.g. 5.0). ratingLabel is formatted "5.0".
 * - total is a Number (e.g. 294). totalLabel = "294", totalPlusLabel = "294+".
 * - Fetches once per page load, cached in localStorage for 6h across pages.
 * - Falls back to sane defaults if the API returns 0/errors — the UI can
 *   still render "5.0 · 294+ reviews" copy safely.
 * - `isLive` is true only when the numbers came from a successful API call
 *   or a fresh cache; false when we're showing the fallback. Callers that
 *   emit AggregateRating JSON-LD must gate on this flag so unverifiable
 *   ratings never reach schema.org (Google can penalize sites for that).
 */
export const useReviewStats = () => {
  const cached = readCache();
  const [stats, setStats] = useState(cached || FALLBACK);
  const [loading, setLoading] = useState(!cached);
  const [isLive, setIsLive] = useState(!!cached);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${base}/api/google-reviews/public`, { timeout: 8000 });
        const rating = Number(res.data?.average_rating) || 0;
        // Prefer the 5-star count (matches the "Five-Star Reviews" label
        // used across the site). Falls back to `total_reviews` when the
        // backend hasn't been redeployed with the new field yet.
        const total = Number(res.data?.five_star_reviews)
                   || Number(res.data?.total_reviews)
                   || 0;
        if (cancelled) return;
        if (rating > 0 && total > 0) {
          setStats({ rating, total });
          setIsLive(true);
          writeCache(rating, total);
        }
      } catch { /* keep fallback/cache */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, []);

  const rating = stats.rating || FALLBACK.rating;
  const total = stats.total || FALLBACK.total;
  return {
    rating,
    total,
    ratingLabel: rating.toFixed(1),
    // No hard-coded count: until the live Google count loads, copy reads "hundreds of"
    totalLabel: total ? `${total}` : "hundreds of",
    totalPlusLabel: total ? `${total}+` : "hundreds of",
    loading,
    isLive,
  };
};

export default useReviewStats;
